import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Globe, Smartphone, Brain, Database, Palette, GraduationCap, ArrowRight, CheckCircle, Zap, Shield, Clock, Terminal } from 'lucide-react';

/* `slug` must stay in sync with the service list on the Home page —
   Home links to /services#<slug> and each card below renders that id. */
const services = [
  {
    Icon: Globe,
    slug: 'web-development',
    title: 'Web Development',
    desc: 'Modern, responsive and high-performance web applications built with React, Node.js and the latest web technologies. From landing pages to full enterprise platforms.',
    color: 'var(--ac-cyan)',
    bg: 'rgba(0,200,255,0.08)',
    tags: ['React', 'Node.js', 'MongoDB', 'REST APIs'],
    features: [
      'Responsive, mobile-first design',
      'SEO-optimised structure',
      'High-performance architecture',
      'Scalable backend systems',
    ],
  },
  {
    Icon: Smartphone,
    slug: 'mobile-app-development',
    title: 'Mobile App Development',
    desc: 'Cross-platform mobile applications for Android and iOS using React Native. Beautiful, fast and user-friendly apps that work seamlessly on all devices.',
    color: 'var(--ac-purple)',
    bg: 'rgba(168,85,247,0.08)',
    tags: ['React Native', 'Android', 'iOS', 'Expo'],
    features: [
      'Cross-platform iOS & Android',
      'Native-like performance',
      'Push notifications & offline support',
      'App store publishing assistance',
    ],
  },
  {
    Icon: Brain,
    slug: 'ai-machine-learning',
    title: 'AI & Machine Learning',
    desc: 'Custom AI models, machine learning pipelines and intelligent automation solutions. We turn your data into powerful insights and smart applications.',
    color: 'var(--ac-green)',
    bg: 'rgba(0,220,130,0.08)',
    tags: ['Python', 'TensorFlow', 'NLP', 'Computer Vision'],
    features: [
      'Custom model training & deployment',
      'Natural language processing',
      'Predictive analytics & forecasting',
      'Intelligent automation workflows',
    ],
  },
  {
    Icon: Database,
    slug: 'data-engineering',
    title: 'Data Engineering',
    desc: 'Data pipelines, analytics dashboards and database architecture designed for performance and insight. We help you collect, process and visualise your data effectively.',
    color: 'var(--ac-pink)',
    bg: 'rgba(224,64,251,0.08)',
    tags: ['SQL', 'Python', 'Power BI', 'ETL'],
    features: [
      'End-to-end data pipeline design',
      'Analytics dashboards & reporting',
      'Database architecture & optimisation',
      'Data cleaning & transformation',
    ],
  },
  {
    Icon: Palette,
    slug: 'ui-ux-design',
    title: 'UI/UX Design',
    desc: 'Beautiful, intuitive and user-centred designs that make your product stand out. From wireframes to fully polished interfaces — we design experiences people love.',
    color: 'var(--ac-cyan)',
    bg: 'rgba(0,200,255,0.08)',
    tags: ['Figma', 'Prototyping', 'User Research', 'Design Systems'],
    features: [
      'User research & persona mapping',
      'Wireframing & interactive prototypes',
      'Design system creation',
      'Usability testing & iteration',
    ],
  },
  {
    Icon: GraduationCap,
    slug: 'altera-scholar-program',
    title: 'Altera Scholar Program',
    desc: 'Our flagship video series for school leavers covering all major IT pathways. Bridging the gap between A/L results and successful tech careers — launching July 2026.',
    color: 'var(--ac-purple)',
    bg: 'rgba(168,85,247,0.08)',
    tags: ['Video Series', 'All IT Paths', 'July 2026', 'Free'],
    features: [
      'All major IT pathways covered',
      'Designed for A/L school leavers',
      'Real-world projects & portfolio',
      'Certificate of completion',
    ],
  },
];

const process = [
  { Icon: Zap, step: '01', title: 'Discovery', desc: 'We start by deeply understanding your goals, requirements and constraints through collaborative planning sessions.' },
  { Icon: Palette, step: '02', title: 'Design', desc: 'Our team crafts wireframes and prototypes, iterating with your feedback before writing a single line of code.' },
  { Icon: Globe, step: '03', title: 'Build', desc: 'We develop your solution using modern technologies, with regular demos and full transparency throughout.' },
  { Icon: Shield, step: '04', title: 'Deliver', desc: 'Rigorous testing, deployment and post-launch support to ensure everything works flawlessly in production.' },
];

const products = [
  {
    id: 'alt-lumina',
    title: 'Alt Lumina',
    subtitle: 'Academic LMS Suite',
    desc: 'Schools and tuition institutes in Sri Lanka often run on a patchwork of tools: one for classes, another for fees, WhatsApp groups for parents, and spreadsheets for everything else. Alt Lumina brings it all into one academic platform.',
    features: [
      'Live online classrooms and a recorded lesson library',
      'Online exams with instant MCQ grading, randomised from a question bank',
      'An AI tutor that answers students in English, Sinhala and Tamil',
      'Early alerts for students who are falling behind',
      'Fee payments through PayHere and iPay, with receipts and financial reports',
      'Parent access on web, Android and iOS, with notifications by SMS, WhatsApp, push and email',
      'Multi-branch management for growing institutes'
    ],
    image: '/images/alt_lumina_logo.jpg',
    color: 'var(--ac-purple)',
    status: 'Launching Soon • Pre-order'
  },
  {
    id: 'alteracart',
    title: 'AlteraCart',
    subtitle: 'E-Commerce Platform',
    desc: 'Selling online in Sri Lanka takes more than a nice product page. You need local payments that work, stock you can trust, and tools your staff can use without calling a developer. AlteraCart is a complete e-commerce platform built around that.',
    features: [
      'Product catalog with colour, finish and size variants, plus filters and search',
      'PayHere and iPay built in, so checkout never depends on a single provider',
      'Stock checked at cart and checkout to prevent overselling',
      'Shipping calculated by delivery region and order weight',
      'Admin panel for products, inventory and orders',
      'Reviews, coupons, automatic order-status alerts and SEO-ready pages'
    ],
    image: '/images/alteracart_logo.jpg',
    color: 'var(--ac-cyan)',
    status: 'Launching Soon • Pre-order'
  },
  {
    id: 'altattendance-pro',
    title: 'AltAttendance Pro',
    subtitle: 'Attendance & Progress',
    desc: "Many tuition classes still teach and test on paper, and that works. What doesn't work is paper registers nobody analyses, marks that reach parents late, and weak students noticed only after the term test. AltAttendance Pro keeps the classroom physical and puts the records online.",
    features: [
      'QR ID-card attendance, about a second per student, with photo verification',
      'Works in any browser on any device, and keeps scanning offline',
      'Enter paper-test marks by grid or Excel, then release them to students and parents',
      'Grade forecasts, student levels and top-10 rankings to spot who needs help early',
      'Lesson-wise progress with a YouTube video library and watch tracking',
      'Automatic SMS or WhatsApp alerts to parents on absences and results'
    ],
    image: '/images/altattendance_logo.jpg',
    color: 'var(--ac-green)',
    status: 'Launching Soon • Pre-order'
  }
];

export default function Services() {
  const [hovered, setHovered] = useState(null);
  const { hash, pathname } = useLocation();

  /* React Router does not restore/scroll on navigation, so handle it here:
     land on the linked service when a #hash is present, otherwise start at the top. */
  useEffect(() => {
    if (!hash) {
      window.scrollTo(0, 0);
      return;
    }
    const id = decodeURIComponent(hash.slice(1));
    // wait one frame so the target is mounted before scrolling
    const raf = requestAnimationFrame(() => {
      const el = document.getElementById(id);
      if (el) {
        el.scrollIntoView({ behavior: 'smooth', block: 'start' });
        setHovered(services.findIndex(s => s.slug === id));
      }
    });
    return () => cancelAnimationFrame(raf);
  }, [hash, pathname]);

  return (
    <div style={{ paddingTop: 68 }}>

      {/* Hero */}
      <section style={{
        padding: '5rem 0 4rem', textAlign: 'center',
        background: `
          radial-gradient(ellipse at 20% 50%, rgba(0,200,255,0.07) 0%, transparent 55%),
          radial-gradient(ellipse at 80% 20%, rgba(168,85,247,0.07) 0%, transparent 55%),
          var(--ac-dark)`,
        position: 'relative', overflow: 'hidden',
      }}>
        <div style={{
          position: 'absolute', inset: 0, opacity: 0.03,
          backgroundImage: 'linear-gradient(rgba(0,200,255,1) 1px, transparent 1px), linear-gradient(90deg, rgba(0,200,255,1) 1px, transparent 1px)',
          backgroundSize: '60px 60px', pointerEvents: 'none',
        }} />
        <div className="container" style={{ position: 'relative', zIndex: 1 }}>
          <div className="badge badge-cyan" style={{ marginBottom: '1.2rem' }}>What We Do</div>
          <h1 style={{ fontSize: 'clamp(32px, 5vw, 60px)', fontWeight: 800, color: 'var(--ac-white)', lineHeight: 1.15, marginBottom: '1.2rem' }}>
            Our <span className="grad-text">Core Services</span>
          </h1>
          <p style={{ fontSize: 17, color: 'var(--ac-muted)', maxWidth: 580, margin: '0 auto 2.5rem', lineHeight: 1.8 }}>
            Real-world technology solutions built by 13+ passionate developers — from web and mobile apps to AI systems and beyond.
          </p>
          <div style={{ display: 'flex', gap: 16, justifyContent: 'center', flexWrap: 'wrap' }}>
            <Link to="/contact" className="btn btn-primary btn-lg">Get a Free Quote <ArrowRight size={18} /></Link>
            <Link to="/educenter" className="btn btn-edu btn-lg">Join EduCenter</Link>
          </div>
        </div>
      </section>

      <div className="glow-divider" />

      {/* Services Grid */}
      <section className="section">
        <div className="container">
          <div className="grid-3">
            {services.map((s, i) => (
              <div key={i} id={s.slug} className="card anchor-target"
                onMouseEnter={() => setHovered(i)}
                onMouseLeave={() => setHovered(null)}
                style={{
                  display: 'flex', flexDirection: 'column',
                  transform: hovered === i ? 'translateY(-6px)' : 'none',
                  borderColor: hovered === i ? s.color + '55' : 'var(--ac-border)',
                  boxShadow: hovered === i ? `0 12px 40px ${s.color}15` : 'none',
                  transition: 'all 0.3s ease',
                }}>
                <div style={{
                  width: 52, height: 52, borderRadius: 'var(--radius-md)',
                  background: s.bg, display: 'flex', alignItems: 'center',
                  justifyContent: 'center', marginBottom: '1.2rem',
                  border: `1px solid ${s.color}25`,
                  transition: 'transform 0.3s',
                  transform: hovered === i ? 'scale(1.1)' : 'scale(1)',
                }}>
                  <s.Icon size={24} color={s.color} />
                </div>
                <h3 style={{ fontSize: 17, fontWeight: 700, color: 'var(--ac-white)', marginBottom: 8 }}>{s.title}</h3>
                <p style={{ fontSize: 13, color: 'var(--ac-muted)', lineHeight: 1.7, flex: 1, marginBottom: '1rem' }}>{s.desc}</p>

                {/* Features */}
                <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginBottom: '1rem' }}>
                  {s.features.map((f, fi) => (
                    <div key={fi} style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12, color: 'var(--ac-text)' }}>
                      <CheckCircle size={13} color={s.color} />
                      {f}
                    </div>
                  ))}
                </div>

                {/* Tags */}
                <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: '0.75rem' }}>
                  {s.tags.map((tag, ti) => (
                    <span key={ti} style={{
                      fontSize: 10, padding: '3px 8px',
                      background: s.color + '12', color: s.color,
                      borderRadius: 20, border: `1px solid ${s.color}25`,
                      fontWeight: 600, letterSpacing: '0.3px',
                    }}>{tag}</span>
                  ))}
                </div>

                <div style={{
                  display: 'flex', alignItems: 'center', gap: 6,
                  color: s.color, fontSize: 13, fontWeight: 500,
                  opacity: hovered === i ? 1 : 0,
                  transform: hovered === i ? 'translateX(0)' : 'translateX(-8px)',
                  transition: 'all 0.3s',
                }}>
                  <Link to="/contact" style={{ color: 'inherit', textDecoration: 'none', display: 'flex', alignItems: 'center', gap: 6 }}>
                    Get a quote <ArrowRight size={14} />
                  </Link>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <div className="glow-divider" />

      {/* Products & Platforms */}
      <section className="section">
        <div className="container">
          <div className="section-header">
            <div className="badge badge-pink" style={{ marginBottom: '1rem' }}>Products & Platforms</div>
            <h2>Software by AlteraCode</h2>
            <p>Our upcoming products built to solve real-world problems in Sri Lanka and beyond.</p>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '3rem' }}>
            {products.map((p, i) => (
              <div key={i} id={p.id} className="card anchor-target" style={{ display: 'flex', flexWrap: 'wrap', gap: '2.5rem', alignItems: 'center' }}>
                <div style={{ flex: '1 1 300px' }}>
                  <div className="badge" style={{ background: p.color + '15', color: p.color, border: `1px solid ${p.color}40`, marginBottom: '1rem' }}>{p.status}</div>
                  <h3 style={{ fontSize: 28, fontWeight: 800, color: 'var(--ac-white)', marginBottom: '0.5rem' }}>{p.title}</h3>
                  <h4 style={{ fontSize: 16, fontWeight: 600, color: p.color, marginBottom: '1rem' }}>{p.subtitle}</h4>
                  <p style={{ fontSize: 15, color: 'var(--ac-muted)', lineHeight: 1.7, marginBottom: '1.5rem' }}>{p.desc}</p>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: '0.75rem' }}>
                    {p.features.map((f, fi) => (
                      <div key={fi} style={{ display: 'flex', gap: 10, alignItems: 'flex-start' }}>
                        <CheckCircle size={16} color={p.color} style={{ flexShrink: 0, marginTop: 4 }} />
                        <span style={{ fontSize: 14, color: 'var(--ac-text)', lineHeight: 1.5 }}>{f}</span>
                      </div>
                    ))}
                  </div>
                  <div style={{ marginTop: '2rem' }}>
                    <Link to="/contact" className="btn btn-outline" style={{ borderColor: p.color, color: p.color }}>
                      Contact for Pre-order <ArrowRight size={16} />
                    </Link>
                  </div>
                </div>
                <div style={{ flex: '1 1 400px', display: 'flex', justifyContent: 'center' }}>
                  <div style={{ width: '100%', maxWidth: 450, borderRadius: 'var(--radius-lg)', overflow: 'hidden', background: 'var(--ac-navy)', display: 'flex', alignItems: 'center', justifyContent: 'center', border: '1px solid var(--ac-border)' }}>
                    <img src={p.image} alt={p.title} style={{ width: '100%', height: 'auto', objectFit: 'contain', padding: '2rem' }} />
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <div className="glow-divider" />

      {/* Music Library */}
      <section id="music-library" className="section anchor-target">
        <div className="container">
          <div className="section-header">
            <div className="badge badge-cyan" style={{ marginBottom: '1rem' }}>Our Music Library</div>
            <h2>Breaking Every Protocol</h2>
            <p style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px' }}>
              From the classroom to the codebase... we don’t just learn, we deploy. <Terminal size={18} />
            </p>
          </div>
          <div className="layout-2col-even" style={{ alignItems: 'center' }}>
            <div>
              <h3 style={{ fontSize: 26, fontWeight: 800, color: 'var(--ac-white)', marginBottom: '1rem' }}>
                Tech Rap Anthem by <span className="grad-text">AlteraCode</span>
              </h3>
              <p style={{ fontSize: 16, color: 'var(--ac-muted)', lineHeight: 1.8, marginBottom: '1.5rem' }}>
                We are proud to present our latest tech rap anthem — <strong>“Breaking Every Protocol”</strong>.
                This project combines music, coding culture, innovation, and futuristic vibes into one powerful experience.
              </p>
              <p style={{ fontSize: 16, color: 'var(--ac-muted)', lineHeight: 1.8, marginBottom: '2rem' }}>
                This track is dedicated to every developer, creator, and dreamer building the future through technology.
                From debugging errors to breaking limits — this is our story.
              </p>
              <a href="https://youtu.be/CJveXpkl4-A?si=UNuhvBwrWr8jooc-" target="_blank" rel="noopener noreferrer" className="btn btn-primary btn-lg">
                Watch on YouTube
              </a>
            </div>
            <div style={{ borderRadius: 'var(--radius-lg)', overflow: 'hidden', boxShadow: 'var(--shadow-glow)', border: '1px solid var(--ac-border)' }}>
              <div style={{ position: 'relative', paddingBottom: '56.25%', height: 0 }}>
                <iframe
                  style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%' }}
                  src="https://www.youtube.com/embed/CJveXpkl4-A"
                  title="Breaking Every Protocol - Rap Anthem for Coders"
                  frameBorder="0"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                  allowFullScreen
                ></iframe>
              </div>
            </div>
          </div>
        </div>
      </section>

      <div className="glow-divider" />

      {/* Process */}
      <section className="section" style={{ background: 'var(--ac-navy)' }}>
        <div className="container">
          <div className="section-header">
            <div className="badge badge-purple" style={{ marginBottom: '1rem' }}>How We Work</div>
            <h2>Our Development Process</h2>
            <p>A transparent, collaborative process designed to deliver great results — every time.</p>
          </div>
          <div className="grid-4">
            {process.map((p, i) => (
              <div key={i} style={{
                background: 'var(--ac-card)', border: '1px solid var(--ac-border)',
                borderRadius: 'var(--radius-lg)', padding: '1.5rem', position: 'relative', overflow: 'hidden',
              }}>
                <div style={{
                  position: 'absolute', top: 12, right: 16,
                  fontSize: 48, fontWeight: 900, color: 'rgba(0,200,255,0.04)', lineHeight: 1,
                }}>{p.step}</div>
                <div style={{
                  width: 44, height: 44, borderRadius: 'var(--radius-md)',
                  background: 'rgba(0,200,255,0.08)', display: 'flex',
                  alignItems: 'center', justifyContent: 'center', marginBottom: '1rem',
                  border: '1px solid rgba(0,200,255,0.2)',
                }}>
                  <p.Icon size={20} color="var(--ac-cyan)" />
                </div>
                <h3 style={{ fontSize: 16, fontWeight: 700, color: 'var(--ac-white)', marginBottom: 6 }}>{p.title}</h3>
                <p style={{ fontSize: 13, color: 'var(--ac-muted)', lineHeight: 1.7 }}>{p.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <div className="glow-divider" />

      {/* CTA */}
      <section className="section-sm">
        <div className="container">
          <div style={{
            background: 'linear-gradient(135deg, rgba(0,200,255,0.08), rgba(168,85,247,0.08))',
            border: '1px solid rgba(0,200,255,0.2)', borderRadius: 'var(--radius-xl)',
            padding: '3.5rem', textAlign: 'center',
          }}>
            <h2 style={{ fontSize: 36, fontWeight: 800, color: 'var(--ac-white)', marginBottom: '0.75rem' }}>
              Ready to <span className="grad-text">Start Building?</span>
            </h2>
            <p style={{ fontSize: 16, color: 'var(--ac-muted)', marginBottom: '2rem', maxWidth: 500, margin: '0 auto 2rem', lineHeight: 1.8 }}>
              Tell us about your project and we'll get back to you within 24 hours with a free consultation.
            </p>
            <div style={{ display: 'flex', gap: 16, justifyContent: 'center', flexWrap: 'wrap' }}>
              <Link to="/contact" className="btn btn-primary btn-lg">Get a Free Quote <ArrowRight size={18} /></Link>
              <Link to="/about" className="btn btn-outline btn-lg">Meet the Team</Link>
            </div>
          </div>
        </div>
      </section>

    </div>
  );
}
